"use client";

/**
 * TrainMock — animated mock of the askFinz Train workspace for /apps/train.
 *
 * Pure presentation: a fine-tuning run dashboard. Job header at top, loss
 * chart streaming points in over time, epoch progress bar, GPU gauge, then
 * a status pill that flips Running → Eval → Done at the end of the script.
 *
 * No recharts dependency — the loss chart is a hand-rolled SVG so we don't
 * pull a charting library into the marketing bundle.
 *
 * NOT the real Train UI (`train.askfinz.ai/app/jobs/*`). Lives in
 * askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Cpu,
  Gauge,
  Database,
  Sparkles,
  CheckCircle2,
  Activity,
  Layers,
  Timer,
} from "lucide-react";

// Hand-crafted curve: descends roughly exponentially, drifts a little.
const LOSS_SEQUENCE: number[] = [
  2.84, 2.51, 2.18, 1.96, 1.74, 1.58, 1.42, 1.31, 1.21, 1.14,
  1.08, 1.02, 0.97, 0.92, 0.88, 0.84, 0.81, 0.78, 0.76, 0.74,
  0.72, 0.70, 0.69, 0.68, 0.67, 0.66, 0.65, 0.64,
];
const TOTAL_POINTS = LOSS_SEQUENCE.length;
const EPOCHS_TOTAL = 12;

type Phase = "running" | "eval" | "done";

// Partial-fill snapshot: ~10 of 28 loss points drawn, epoch 4/12, GPU warm.
// The chart looks "mid-run" on first paint, not empty.
const INITIAL_POINTS = 10;
const INITIAL_EPOCH = Math.round((INITIAL_POINTS / TOTAL_POINTS) * EPOCHS_TOTAL);
const INITIAL_ELAPSED = INITIAL_POINTS * 2;
const INITIAL_GPU = 78;

export function TrainMock() {
  const [points, setPoints] = useState(INITIAL_POINTS);
  const [epoch, setEpoch] = useState(INITIAL_EPOCH);
  const [gpu, setGpu] = useState(INITIAL_GPU);
  const [phase, setPhase] = useState<Phase>("running");
  const [elapsed, setElapsed] = useState(INITIAL_ELAPSED); // minutes
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      // Reset to the partial-fill snapshot (not zero).
      setPoints(INITIAL_POINTS);
      setEpoch(INITIAL_EPOCH);
      setGpu(INITIAL_GPU);
      setElapsed(INITIAL_ELAPSED);
      setPhase("running");

      // Stream the remaining loss points from INITIAL_POINTS → TOTAL_POINTS.
      for (let i = INITIAL_POINTS; i < TOTAL_POINTS; i += 1) {
        schedule(() => {
          setPoints(i + 1);
          setEpoch(Math.min(EPOCHS_TOTAL, Math.round(((i + 1) / TOTAL_POINTS) * EPOCHS_TOTAL)));
          setElapsed((i + 1) * 2);
        }, 180 * (i - INITIAL_POINTS) + 250);
      }

      // GPU oscillates ~78%
      const gpuValues = [62, 71, 78, 81, 76, 80, 79, 77, 78, 82, 79, 78, 80, 77, 81, 78, 76, 79];
      gpuValues.forEach((v, i) => {
        schedule(() => setGpu(v), 350 + i * 400);
      });

      // Transition phases — measured from the point the script starts.
      const evalAt = 180 * (TOTAL_POINTS - INITIAL_POINTS) + 600;
      schedule(() => setPhase("eval"), evalAt);
      schedule(() => setPhase("done"), evalAt + 1800);

      // Loop after done dwell.
      schedule(runCycle, evalAt + 1800 + 3000);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  // Build path for the loss line + an area fill below it.
  const chartW = 520;
  const chartH = 180;
  const padL = 38;
  const padR = 12;
  const padT = 14;
  const padB = 24;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const lossMax = LOSS_SEQUENCE[0]; // ~2.84
  const lossMin = 0.5;
  const xFor = (i: number) => padL + (i / (TOTAL_POINTS - 1)) * innerW;
  const yFor = (v: number) => padT + ((lossMax - v) / (lossMax - lossMin)) * innerH;

  const visiblePoints = LOSS_SEQUENCE.slice(0, points);
  const linePath = visiblePoints
    .map((v, i) => `${i === 0 ? "M" : "L"}${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`)
    .join(" ");
  const areaPath =
    visiblePoints.length > 1
      ? `${linePath} L${xFor(visiblePoints.length - 1).toFixed(1)},${(padT + innerH).toFixed(1)} L${xFor(0).toFixed(1)},${(padT + innerH).toFixed(1)} Z`
      : "";

  const currentLoss = visiblePoints[visiblePoints.length - 1] ?? LOSS_SEQUENCE[0];
  const epochPct = Math.min(100, (epoch / EPOCHS_TOTAL) * 100);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            train.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (phase === "running"
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : phase === "eval"
                  ? "bg-foreground/70 animate-pulse"
                  : "bg-foreground/70")
              }
            />
            {phase === "running" ? "Running" : phase === "eval" ? "Eval" : "Done"}
          </span>
        </div>

        {/* Job header */}
        <div className="px-4 py-3 border-b hairline bg-foreground/[0.015]">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border hairline-strong bg-background">
              <Sparkles className="h-4 w-4 text-foreground/65" />
            </span>
            <div className="min-w-0">
              <div className="text-[13px] font-medium truncate">support-tone · LoRA fine-tune</div>
              <div className="text-[11px] text-foreground/65 font-mono truncate">
                base: open-weight-70B · dataset: tickets-cleaned-9.4k · started 03:42 ago
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Timer className="h-3 w-3" />
              {Math.floor(elapsed / 60).toString().padStart(2, "0")}:{(elapsed % 60).toString().padStart(2, "0")}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[440px]">
          {/* Loss chart + epoch */}
          <div className="col-span-12 md:col-span-8 p-5 border-r hairline">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-3">
              <Activity className="h-3 w-3" />
              <span>Training loss</span>
              <span className="ml-auto font-mono text-foreground/85 text-[12px]">
                {currentLoss.toFixed(3)}
              </span>
            </div>

            <div className="rounded-xl border hairline bg-background p-2">
              <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-auto" preserveAspectRatio="none">
                {/* Y-axis grid */}
                {[0, 0.5, 1].map((t) => {
                  const y = padT + t * innerH;
                  const v = lossMax - t * (lossMax - lossMin);
                  return (
                    <g key={t}>
                      <line
                        x1={padL}
                        x2={chartW - padR}
                        y1={y}
                        y2={y}
                        stroke="currentColor"
                        strokeOpacity="0.08"
                        strokeDasharray="2 4"
                      />
                      <text
                        x={padL - 6}
                        y={y + 3}
                        fontSize="9"
                        fontFamily="ui-monospace, monospace"
                        textAnchor="end"
                        fill="currentColor"
                        fillOpacity="0.45"
                      >
                        {v.toFixed(1)}
                      </text>
                    </g>
                  );
                })}
                {/* X-axis */}
                <line
                  x1={padL}
                  x2={chartW - padR}
                  y1={chartH - padB}
                  y2={chartH - padB}
                  stroke="currentColor"
                  strokeOpacity="0.18"
                />
                {[0, 0.25, 0.5, 0.75, 1].map((t) => {
                  const x = padL + t * innerW;
                  const step = Math.round(t * (TOTAL_POINTS - 1));
                  return (
                    <text
                      key={t}
                      x={x}
                      y={chartH - padB + 14}
                      fontSize="9"
                      fontFamily="ui-monospace, monospace"
                      textAnchor="middle"
                      fill="currentColor"
                      fillOpacity="0.45"
                    >
                      step {step * 50}
                    </text>
                  );
                })}
                {/* Area fill */}
                {areaPath && (
                  <path d={areaPath} fill="currentColor" fillOpacity="0.06" />
                )}
                {/* Line */}
                <path
                  d={linePath}
                  fill="none"
                  stroke="currentColor"
                  strokeOpacity="0.85"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Latest point dot */}
                {visiblePoints.length > 0 && (
                  <circle
                    cx={xFor(visiblePoints.length - 1)}
                    cy={yFor(visiblePoints[visiblePoints.length - 1])}
                    r="3"
                    fill="hsl(var(--accent))"
                  >
                    <animate attributeName="r" values="3;4.5;3" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                )}
              </svg>
            </div>

            {/* Epoch progress */}
            <div className="mt-5">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-2">
                <Layers className="h-3 w-3" />
                Epochs
                <span className="ml-auto font-mono text-foreground/85 text-[11px]">
                  {epoch} / {EPOCHS_TOTAL}
                </span>
              </div>
              <div className="relative h-2.5 rounded-full bg-foreground/[0.06] overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-foreground/85 rounded-full transition-[width] duration-500 ease-out"
                  style={{ width: `${epochPct}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-mono text-foreground/65">
                <span>start</span>
                <span>{Math.round(epochPct)}%</span>
                <span>finish</span>
              </div>
            </div>

            {/* Hyperparam strip */}
            <div className="mt-5 grid grid-cols-3 gap-3 text-[11px]">
              <Stat label="learning rate" value="2e-4" />
              <Stat label="batch size"   value="16" />
              <Stat label="seq length"   value="4096" />
            </div>
          </div>

          {/* Right rail */}
          <div className="col-span-12 md:col-span-4 flex flex-col">
            {/* GPU gauge */}
            <div className="p-5 border-b hairline">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-2">
                <Cpu className="h-3 w-3" />
                GPU
              </div>
              <GpuGauge value={gpu} />
              <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                <div className="rounded border hairline px-2 py-1.5">
                  <div className="text-foreground/65 uppercase tracking-[0.14em]">vRAM</div>
                  <div className="font-mono text-foreground/85 mt-0.5">68 / 80 GB</div>
                </div>
                <div className="rounded border hairline px-2 py-1.5">
                  <div className="text-foreground/65 uppercase tracking-[0.14em]">temp</div>
                  <div className="font-mono text-foreground/85 mt-0.5">71 °C</div>
                </div>
              </div>
            </div>

            {/* Eval / Done summary */}
            <div className="p-5 flex-1">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-3">
                <Database className="h-3 w-3" />
                Evaluation
              </div>
              <div className="space-y-2">
                <EvalRow label="eval loss"   value={phase === "done" ? "0.71" : phase === "eval" ? "…" : "—"} done={phase === "done"} />
                <EvalRow label="perplexity"  value={phase === "done" ? "2.03" : phase === "eval" ? "…" : "—"} done={phase === "done"} />
                <EvalRow label="tone match"  value={phase === "done" ? "0.87" : phase === "eval" ? "…" : "—"} done={phase === "done"} />
              </div>

              {phase === "done" && (
                <div className="mt-4 rounded-lg border hairline-strong bg-foreground/[0.03] p-3 inline-flex items-center gap-2 animate-[blur-fade-in_0.5s_ease-out]">
                  <CheckCircle2 className="h-3.5 w-3.5 text-foreground/75" />
                  <div className="min-w-0">
                    <div className="text-[12px] text-foreground/85">Ready to deploy</div>
                    <div className="text-[10px] font-mono text-foreground/65 truncate">support-tone:v3 · 1.2 GB</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border hairline px-3 py-2 bg-background">
      <div className="text-[10px] uppercase tracking-[0.16em] text-foreground/65">{label}</div>
      <div className="mt-1 font-mono text-foreground/85">{value}</div>
    </div>
  );
}

function EvalRow({ label, value, done }: { label: string; value: string; done: boolean }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-foreground/65">{label}</span>
      <span className={"font-mono " + (done ? "text-foreground/90" : "text-foreground/65")}>{value}</span>
    </div>
  );
}

function GpuGauge({ value }: { value: number }) {
  // Half-donut gauge from 0..100. Sweep 180° (left to right) over 80px height.
  const r = 56;
  const cx = 70;
  const cy = 60;
  const start = { x: cx - r, y: cy };
  const end = { x: cx + r, y: cy };
  const valClamped = Math.max(0, Math.min(100, value));
  const angle = Math.PI - (valClamped / 100) * Math.PI;
  const px = cx + r * Math.cos(angle);
  const py = cy - r * Math.sin(angle);
  return (
    <div className="relative">
      <svg viewBox="0 0 140 80" className="w-full h-auto">
        {/* Track */}
        <path
          d={`M${start.x},${start.y} A${r},${r} 0 0 1 ${end.x},${end.y}`}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.12"
          strokeWidth="8"
          strokeLinecap="round"
        />
        {/* Value arc */}
        {value > 0 && (
          <path
            d={`M${start.x},${start.y} A${r},${r} 0 0 1 ${px.toFixed(2)},${py.toFixed(2)}`}
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.85"
            strokeWidth="8"
            strokeLinecap="round"
            style={{ transition: "all 0.5s ease-out" }}
          />
        )}
        <text
          x={cx}
          y={cy - 6}
          fontSize="20"
          fontFamily="ui-monospace, monospace"
          textAnchor="middle"
          fill="currentColor"
          fillOpacity="0.9"
        >
          {valClamped}%
        </text>
        <text
          x={cx}
          y={cy + 10}
          fontSize="9"
          fontFamily="ui-monospace, monospace"
          textAnchor="middle"
          fill="currentColor"
          fillOpacity="0.5"
          letterSpacing="1.5"
        >
          UTIL
        </text>
      </svg>
      <Gauge className="absolute top-1 right-1 h-3 w-3 text-foreground/30" aria-hidden />
    </div>
  );
}
